import { useState } from "react";

import { useMutation } from "@apollo/client";
import {
  Button,
  Callout,
  FormGroup,
  Icon,
  InputGroup,
} from "@blueprintjs/core";
import idx from "idx";

import { Box, Flex } from "@/components/layout/flexbox";
import CircleIconButton from "@/components/pieces/form/CircleIconButton";
import { BRAND_PURPLE } from "@/css/constants";
import { RedeemGiftCodeDocument } from "@/graphql";

export default function GiftCodeSelector({
  order,
  loading,
  paymentMethodId,
  setOrder,
  orderCreditTotal,
  onCompletedRedeemGiftCode = () => {},
}) {
  const [showingForm, setShowingForm] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [redeem, { loading: submitting }] = useMutation(
    RedeemGiftCodeDocument,
    {
      onCompleted: (r) => {
        if (r.errors) {
          setError(
            "Whoops, something went wrong. Please refresh and try again.",
          );
          return;
        }
        if (
          r &&
          r.redeemGiftCode &&
          r.redeemGiftCode.errors.length &&
          showingForm
        ) {
          setError(r.redeemGiftCode.errors[0]);
        } else {
          setCode("");
          setShowingForm(false);
          setSuccess(true);
          setOrder(r.redeemGiftCode.order);
          if (
            (idx(r, (_) => _.redeemGiftCode.order.user.availableCredits) ||
              0) >= orderCreditTotal &&
            orderCreditTotal > 0
          ) {
            onCompletedRedeemGiftCode();
          }
        }
      },
    },
  );

  function handleSubmit() {
    setError(null);
    redeem({
      variables: {
        orderId: order.id,
        code: code.trim(),
      },
    });
  }

  if (showingForm) {
    return (
      <Box>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex flexWrap="wrap">
            <FormGroup
              inline={true}
              label="Gift Code:"
              labelFor="gift-code"
              style={{ margin: 0, alignItems: "center" }}
            >
              <InputGroup
                autoFocus={true}
                value={code}
                name="gift-code"
                placeholder="Enter Gift Code"
                onChange={(e) => setCode(e.target.value)}
                large={true}
                fill={false}
                required={true}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  }
                }}
              />
            </FormGroup>
            <Box ml={3}>
              <Button
                intent="primary"
                large={true}
                disabled={loading || submitting || !code}
                loading={submitting}
                onClick={() => handleSubmit()}
              >
                Redeem
              </Button>
            </Box>
          </Flex>
          <CircleIconButton
            icon="cross"
            iconSize={24}
            size={36}
            onClick={() => setShowingForm(false)}
          />
        </Flex>
        {error && error.message && (
          <Box mt={3}>
            <Callout intent="warning" title="Could not redeem gift code">
              {error.message}
            </Callout>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <>
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          setShowingForm(true);
          setSuccess(false);
        }}
      >
        <Box color={BRAND_PURPLE} fontWeight={500}>
          <Icon icon="plus" /> Use gift code
        </Box>
      </a>
      {success && (
        <Box mt={3}>
          <Callout intent="success" title="Gift code redeemed">
            Your gift code was successfully redeemed.
            {paymentMethodId !== "CREDITS"
              ? ' To use your credits, please set your payment method to "Credits".'
              : null}
          </Callout>
        </Box>
      )}
    </>
  );
}
